import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { rupiah, fmtDateTime } from "../lib/format";
import { RupiahInput } from "../components/RupiahInput";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, ArrowUpCircle, ArrowDownCircle, Pencil, Wallet, FileDown } from "lucide-react";
import { exportFinancePDF } from "../lib/pdf";
import { toast } from "sonner";

export default function Finance() {
  const { user, isViewOnly } = useAuth();
  const [cash, setCash] = useState([]);
  const [form, setForm] = useState(null);
  const [bal, setBal] = useState(null);
  const [balForm, setBalForm] = useState(null);
  const canEditBal = ["master", "admin"].includes(user?.role);

  const [range, setRange] = useState({ from: "", to: "" });
  const [periodOpening, setPeriodOpening] = useState(null);

  const load = () => api.get("/cash-book").then((r) => setCash(r.data));
  const loadBal = () => api.get("/daily-balance/today").then((r) => setBal(r.data.record));
  useEffect(() => { load(); loadBal(); }, []);
  useEffect(() => {
    if (!range.from) { setPeriodOpening(null); return; }
    api.get(`/daily-balance/by-date/${range.from}`).then((r) => setPeriodOpening(r.data.record?.amount ?? 0)).catch(() => setPeriodOpening(0));
  }, [range.from]);

  const dateOf = (c) => (c.date || (c.created_at || "").slice(0, 10));
  const inRange = (c) => {
    const d = dateOf(c);
    if (range.from && d < range.from) return false;
    if (range.to && d > range.to) return false;
    return true;
  };
  const filteredCash = cash.filter(inRange);
  const income = filteredCash.filter((c) => c.type === "in").reduce((s, c) => s + c.amount, 0);
  const expense = filteredCash.filter((c) => c.type === "out").reduce((s, c) => s + c.amount, 0);
  const activeFilter = !!(range.from || range.to);
  const effectiveOpening = activeFilter ? (periodOpening || 0) : (bal?.amount || 0);
  const rangeLabel = activeFilter ? `${range.from || "awal"} s/d ${range.to || "akhir"}` : "Semua transaksi";

  const save = async () => {
    try { await api.post("/cash-book", form); toast.success("Tercatat"); setForm(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Hapus catatan buku kas ini?")) return; await api.delete(`/cash-book/${id}`); load(); };
  const saveBal = async () => {
    try { await api.put("/daily-balance", { amount: balForm.amount }); toast.success("Saldo awal diperbarui"); setBalForm(null); loadBal(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="finance-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display text-slate-900">Buku Kas</h1>
          <p className="text-sm text-slate-500">Pemasukan & pengeluaran operasional harian</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => exportFinancePDF({ records: filteredCash, income, expense, opening: effectiveOpening }, rangeLabel)} data-testid="export-cashbook-pdf-button"><FileDown className="h-4 w-4 mr-1.5" /> Unduh PDF</Button>
          {!isViewOnly && <Button onClick={() => setForm({ type: "out", amount: 0, category: "", description: "" })} className="bg-orange-500 hover:bg-orange-600" data-testid="add-cash-button"><Plus className="h-4 w-4 mr-1.5" /> Catat Kas</Button>}
        </div>
      </div>

      <Card className="border-orange-200 bg-orange-50/60">
        <CardContent className="p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-orange-500 flex items-center justify-center shrink-0"><Wallet className="h-5 w-5 text-white" /></div>
            <div>
              <div className="text-xs text-slate-500">Saldo Awal Hari Ini</div>
              <div className="text-2xl font-bold mono text-slate-900 mt-0.5" data-testid="finance-opening-balance">{bal ? rupiah(bal.amount) : "Belum diisi"}</div>
              {bal?.updated_by && <div className="text-[11px] text-slate-400 mt-0.5">Terakhir diubah oleh {bal.updated_by}</div>}
            </div>
          </div>
          {canEditBal && bal && (
            <Button variant="outline" onClick={() => setBalForm({ amount: bal.amount })} data-testid="edit-opening-balance-button"
              className="border-orange-300 text-orange-700 hover:bg-orange-100">
              <Pencil className="h-4 w-4 mr-1.5" /> Ubah
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-3">
        <div className="space-y-1"><Label className="text-xs">Dari Tanggal</Label><Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="w-44" data-testid="cashbook-from-date" /></div>
        <div className="space-y-1"><Label className="text-xs">Sampai Tanggal</Label><Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="w-44" data-testid="cashbook-to-date" /></div>
        {activeFilter && <Button variant="ghost" onClick={() => setRange({ from: "", to: "" })} data-testid="cashbook-clear-filter" className="text-slate-500">Reset</Button>}
        <div className="text-xs text-slate-400 ml-auto">Periode: <b className="text-slate-600">{rangeLabel}</b></div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs text-slate-500">Pemasukan</div><div className="text-xl font-bold mono text-emerald-600 mt-1">{rupiah(income)}</div></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs text-slate-500">Pengeluaran</div><div className="text-xl font-bold mono text-rose-600 mt-1">{rupiah(expense)}</div></CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs text-slate-500">Saldo Kas</div><div className="text-xl font-bold mono text-slate-900 mt-1">{rupiah(effectiveOpening + income - expense)}</div></CardContent></Card>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50"><TableHead>Tanggal</TableHead><TableHead>Jenis</TableHead><TableHead>Kategori</TableHead><TableHead>Keterangan</TableHead><TableHead className="text-right">Nominal</TableHead>{!isViewOnly && <TableHead></TableHead>}</TableRow></TableHeader>
          <TableBody>
            {filteredCash.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="text-xs text-slate-500">{fmtDateTime(c.created_at)}</TableCell>
                <TableCell><Badge className={c.type === "in" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : "bg-rose-100 text-rose-700 hover:bg-rose-100"}>{c.type === "in" ? "Masuk" : "Keluar"}</Badge></TableCell>
                <TableCell>{c.category || "-"}</TableCell><TableCell className="text-slate-500">{c.description || "-"}</TableCell>
                <TableCell className="text-right mono font-semibold">{rupiah(c.amount)}</TableCell>
                {!isViewOnly && <TableCell className="text-right"><Button size="icon" variant="ghost" className="text-rose-500" onClick={() => del(c.id)}><Trash2 className="h-4 w-4" /></Button></TableCell>}
              </TableRow>
            ))}
            {filteredCash.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">{activeFilter ? "Tidak ada catatan kas pada periode ini" : "Belum ada catatan kas"}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Catat Kas</DialogTitle></DialogHeader>
          {form && <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setForm({ ...form, type: "in" })} className={`py-2.5 rounded-lg border flex items-center justify-center gap-1.5 text-sm font-medium ${form.type === "in" ? "bg-emerald-500 text-white border-emerald-500" : "border-slate-200"}`}><ArrowUpCircle className="h-4 w-4" /> Masuk</button>
              <button onClick={() => setForm({ ...form, type: "out" })} className={`py-2.5 rounded-lg border flex items-center justify-center gap-1.5 text-sm font-medium ${form.type === "out" ? "bg-rose-500 text-white border-rose-500" : "border-slate-200"}`}><ArrowDownCircle className="h-4 w-4" /> Keluar</button>
            </div>
            <div className="space-y-1"><Label>Nominal</Label><RupiahInput value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} data-testid="cash-amount-input" /></div>
            <div className="space-y-1"><Label>Kategori</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Contoh: Listrik, Gaji, Transport" /></div>
            <div className="space-y-1"><Label>Keterangan</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>}
          <DialogFooter><Button onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid="save-cash-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!balForm} onOpenChange={(o) => !o && setBalForm(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Ubah Saldo Awal Harian</DialogTitle></DialogHeader>
          {balForm && <div className="space-y-2">
            <Label>Nominal Saldo Awal</Label>
            <RupiahInput value={balForm.amount} onChange={(v) => setBalForm({ ...balForm, amount: v })} data-testid="opening-balance-input" autoFocus />
            <p className="text-xs text-slate-500">Koreksi saldo kas awal jika terjadi salah input atau penyesuaian modal di tengah hari. Hanya Master &amp; Admin yang dapat mengubah.</p>
          </div>}
          <DialogFooter><Button onClick={saveBal} className="bg-orange-500 hover:bg-orange-600" data-testid="save-opening-balance-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
